import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext.js";
import "./styles/AdminDashboard.css";

const ROLE_OPTIONS = [
  { value: "", label: "All Roles" },
  { value: "admin", label: "Admins" },
  { value: "journalist", label: "Journalists" },
  { value: "reporter", label: "Reporters" },
  { value: "user", label: "Subscribers" },
  { value: "freemium", label: "Freemium" }
];

const AdminDashboard = () => {
  const { token } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [roleFilter, setRoleFilter] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const resp = await fetch("/api/admin/users", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        });
        if (!resp.ok) throw new Error(`Failed to fetch users: ${resp.status}`);
        const data = await resp.json();
        setUsers(data);
      } catch (err) {
        console.error("Error loading users:", err);
        setError(err.message);
      }
    };
    fetchUsers();
  }, [token]);

  const filtered = roleFilter
    ? users.filter((u) => u.role === roleFilter)
    : users;

  const roleCounts = users.reduce((acc, u) => {
    acc[u.role] = (acc[u.role] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="admin-layout">
      <div className="admin-layout-inner">
        <div className="admin-page-header">
          <h1>Admin Dashboard</h1>
          <p>Manage users, roles, and account settings</p>
        </div>

        {error && <div className="admin-alert admin-alert-error">{error}</div>}

        <div className="admin-stat-grid">
          <div className="admin-stat-card">
            <div className="admin-stat-label">Total Users</div>
            <div className="admin-stat-value">{users.length}</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-label">Subscribers</div>
            <div className="admin-stat-value">{roleCounts.user || 0}</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-label">Free Accounts</div>
            <div className="admin-stat-value">{roleCounts.freemium || 0}</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-label">Staff</div>
            <div className="admin-stat-value">{(roleCounts.admin || 0) + (roleCounts.journalist || 0) + (roleCounts.reporter || 0)}</div>
          </div>
        </div>

        <div className="admin-toolbar">
          <div className="admin-toolbar-left">
            <span className="admin-filter-label">Filter</span>
            <select
              className="admin-select"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="admin-toolbar-right">
            <Link to="/admin/users/add" className="admin-btn admin-btn-primary">Add User</Link>
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Username</th>
                  <th>Display Name</th>
                  <th>Role</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length > 0 ? (
                  filtered.map((u) => (
                    <tr key={u.id}>
                      <td>{u.id}</td>
                      <td style={{ fontWeight: 500 }}>{u.username}</td>
                      <td>{u.display_name || "—"}</td>
                      <td>
                        <span className={`admin-badge admin-badge-${u.role}`}>{u.role}</span>
                      </td>
                      <td>{new Date(u.created_at).toLocaleDateString()}</td>
                      <td>
                        <Link to={`/admin/users/edit/${u.id}`} className="admin-link">Edit</Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="admin-empty">No users found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
